// src/phase4/data/tower-configs.ts
// Tower configuration data - 3 upgrade paths per GDD-MVP design

import { TowerUpgradePath, TowerType } from '../types/tower';

// CPU Main Tower Upgrade Path: 8086 → 386 → Pentium → Core → Zen/Apple M
// Focus: Base DPS and skill form
export const CPU_TOWER_PATH: TowerUpgradePath = {
  towerType: TowerType.CPU_MAIN,
  pathName: 'CPU',
  levels: [
    {
      level: 1,
      name: '8086',
      dps: 10,
      range: 120,
      fireRate: 1000,
      upgradeCost: 100,
      textureKey: 'cpu_1',  // Maps to public/assets/phase4/towers/cpu_1.png
    },
    {
      level: 2,
      name: '386',
      dps: 18,
      range: 140,
      fireRate: 900,
      specialEffects: [{ type: 'critical', value: 0.1 }],
      upgradeCost: 200,
      textureKey: 'cpu_2',  // Maps to public/assets/phase4/towers/cpu_2.png
    },
    {
      level: 3,
      name: 'Pentium',
      dps: 28,
      range: 160,
      fireRate: 800,
      specialEffects: [{ type: 'critical', value: 0.15 }],
      upgradeCost: 400,
      textureKey: 'cpu_3',  // Maps to public/assets/phase4/towers/cpu_3.png
    },
    {
      level: 4,
      name: 'Core',
      dps: 42,
      range: 180,
      fireRate: 700,
      specialEffects: [
        { type: 'critical', value: 0.2 },
        { type: 'aoe', value: 0.3 },
      ],
      upgradeCost: 700,
      textureKey: 'cpu_4',  // Maps to public/assets/phase4/towers/cpu_4.png
    },
    {
      level: 5,
      name: 'Zen',
      dps: 60,
      range: 200,
      fireRate: 600,
      specialEffects: [
        { type: 'critical', value: 0.25 },
        { type: 'aoe', value: 0.5 },
      ],
      upgradeCost: 1200,
      textureKey: 'cpu_5',  // Maps to public/assets/phase4/towers/cpu_5.png
    },
  ],
};

// Language Side Tower Paths: C → C++ → Java → Python/JS → Rust/Go
// Focus: Damage/Skill multiplier
export const LANGUAGE_TOWER_PATHS: TowerUpgradePath[] = [
  {
    towerType: TowerType.LANGUAGE_SIDE,
    pathName: 'C',
    levels: [
      {
        level: 1,
        name: 'C',
        dps: 8,
        range: 100,
        fireRate: 1200,
        upgradeCost: 80,
        textureKey: 'lang_1',
      },
      {
        level: 2,
        name: 'C++',
        dps: 14,
        range: 110,
        fireRate: 1100,
        specialEffects: [{ type: 'critical', value: 0.12 }],
        upgradeCost: 150,
        textureKey: 'lang_2',
      },
      {
        level: 3,
        name: 'Java',
        dps: 22,
        range: 120,
        fireRate: 1000,
        specialEffects: [{ type: 'critical', value: 0.18 }],
        upgradeCost: 300,
        textureKey: 'lang_3',
      },
      {
        level: 4,
        name: 'Python',
        dps: 32,
        range: 130,
        fireRate: 900,
        specialEffects: [
          { type: 'critical', value: 0.22 },
          { type: 'aoe', value: 0.25 },
        ],
        upgradeCost: 550,
        textureKey: 'lang_4',
      },
      {
        level: 5,
        name: 'Rust',
        dps: 45,
        range: 140,
        fireRate: 800,
        specialEffects: [
          { type: 'critical', value: 0.3 },
          { type: 'aoe', value: 0.4 },
        ],
        upgradeCost: 1000,
        textureKey: 'lang_5',
      },
    ],
  },
];

// Network Side Tower Paths: Apache → Nginx → TLS → WAF → k8s
// Focus: Range/Pierce/Slow/Shield
export const NETWORK_TOWER_PATHS: TowerUpgradePath[] = [
  {
    towerType: TowerType.NETWORK_SIDE,
    pathName: 'Apache',
    levels: [
      {
        level: 1,
        name: 'Apache',
        dps: 5,
        range: 180,
        fireRate: 1500,
        specialEffects: [{ type: 'slow', value: 0.15 }],
        upgradeCost: 80,
        textureKey: 'net_1',
      },
      {
        level: 2,
        name: 'Nginx',
        dps: 8,
        range: 200,
        fireRate: 1400,
        specialEffects: [{ type: 'slow', value: 0.25 }],
        upgradeCost: 150,
        textureKey: 'net_2',
      },
      {
        level: 3,
        name: 'TLS',
        dps: 12,
        range: 220,
        fireRate: 1300,
        specialEffects: [
          { type: 'slow', value: 0.3 },
          { type: 'pierce', value: 0.15 },
        ],
        upgradeCost: 280,
        textureKey: 'net_3',
      },
      {
        level: 4,
        name: 'WAF',
        dps: 18,
        range: 240,
        fireRate: 1200,
        specialEffects: [
          { type: 'slow', value: 0.35 },
          { type: 'pierce', value: 0.25 },
        ],
        upgradeCost: 500,
        textureKey: 'net_4',
      },
      {
        level: 5,
        name: 'k8s',
        dps: 25,
        range: 260,
        fireRate: 1100,
        specialEffects: [
          { type: 'slow', value: 0.4 },
          { type: 'pierce', value: 0.35 },
          { type: 'shield', value: 0.2 },
        ],
        upgradeCost: 900,
        textureKey: 'net_5',
      },
    ],
  },
];

// Export all tower paths
export const ALL_TOWER_PATHS: TowerUpgradePath[] = [
  CPU_TOWER_PATH,
  ...LANGUAGE_TOWER_PATHS,
  ...NETWORK_TOWER_PATHS,
];

// Get upgrade path for a tower type
export function getUpgradePath(towerType: TowerType, pathName?: string): TowerUpgradePath | undefined {
  return ALL_TOWER_PATHS.find(
    (path) => path.towerType === towerType && (!pathName || path.pathName === pathName)
  );
}

// Get level config for a tower
export function getTowerLevelConfig(
  towerType: TowerType,
  pathName: string,
  level: number
) {
  const path = getUpgradePath(towerType, pathName);
  return path?.levels[level - 1];
}
